import { ComponentProps, lazy, Suspense } from 'react';
import {
  PaymentEntryType,
  PaymentOption,
  CardInfo,
  PaymentMethodId,
  PaymentMethodTypeEnum,
  Amount,
  UITransactionRequest,
  PartialPaymentTransaction,
  BankAccountDetails,
  BankAccountUsage,
  BankDetails,
  PaymentTransaction,
  PaymentTransactionStatus,
} from '@commerce-payment-webcomponents/sdk';
import { cmsApi } from '@commerce-webcomponents/sdk';
import { type AppState, ConsoleLogger, formatPrice } from '@nextgen-web-framework/core';
import { Contact } from '@commerce-profile-webcomponents/sdk';
import { AccountStatusColorEnum } from '../components/bank-account-container/types/propTypes';
import { BankDetailCMSResponseType } from './types/propTypes';

const getTransformedPaymentOptions = (paymentOptions: PaymentOption[]): { [key: string]: PaymentMethodTypeEnum } => {
  const transformedOptions: {
    [key: string]: PaymentMethodTypeEnum;
  } = {};
  paymentOptions?.forEach(
    (option) =>
      (transformedOptions[option?.paymentMethodProviderId] = option.paymentMethod?.type as PaymentMethodTypeEnum),
  );
  return transformedOptions;
};

export const formatCardNo = (cardNo: string) => {
  let result: string = cardNo;
  if (cardNo) {
    const last4Digits = cardNo.slice(-4);
    const maskedNumber = last4Digits.padStart(cardNo.length, '*');
    result = maskedNumber.replace(/(.{4})/g, '$1 ').trim();
  }
  return result;
};

export const isCardExpired = (panExpiry?: string) => {
  let expired = false;
  const now: Date = new Date();
  const creditExpiryDate = new Date();
  if (panExpiry) {
    const month: string = panExpiry.slice(0, 2);
    const year: string = panExpiry.slice(2, 4);
    creditExpiryDate.setFullYear(parseInt(`20${year}`), parseInt(month), 1);
    if (now > creditExpiryDate) {
      expired = true;
    }
  }

  return expired;
};

export const formatNumber2Decimal = (value: number) => (!Number.isNaN(value) ? Math.round(value * 100) / 100 : 0);

export const getCurrencySymbol = () => '฿';

export const isValidHttpUrl = (str: string) => {
  let url: URL;
  try {
    url = new URL(str);
  } catch (e) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

export const formatAmpoints = ({
  points,
  pointsRatio,
  currency,
}: {
  points: number;
  pointsRatio: number;
  currency: string;
}) => `${formatPrice(points / pointsRatio, currency)} (${points.toLocaleString()})`;

export function getPaymentOptionByID(paymentOptions: PaymentOption[], id: string) {
  return paymentOptions.find((pmp: PaymentOption) => pmp?.paymentMethod?.code === id);
}

export function toMsg(transactions?: UITransactionRequest[] | UITransactionRequest) {
  if (!transactions) {
    return undefined;
  }
  const result: PartialPaymentTransaction[] = !Array.isArray(transactions) ? [transactions] : transactions;
  return result.map((t) => `${t.paymentMethodProviderId}: ${t.amount?.value}`);
}

export function getTransactionCardInfo(
  transaction: PartialPaymentTransaction,
  creditPmp?: PaymentOption,
):
  | {
      cardNo: string;
      cardSchemeCode: string;
      noOfInstallments?: number;
      interestRate?: number;
    }
  | undefined {
  //TODO should not use token to get card info ,card info should save in transaction entry
  const initEntry = transaction.entries?.find((e) => e.entryType == PaymentEntryType.Initiate);
  const authEntry = transaction.entries?.find((e) => e.entryType == PaymentEntryType.Auth);
  const captureEntry = transaction.entries?.find((e) => e.entryType == PaymentEntryType.Capture);
  //this part should be standard, todo save cardNo for saved card and INIT
  const cardNo = captureEntry?.payload?.cardNo || authEntry?.payload?.cardNo || initEntry?.payload?.cardNo;
  const cardSchemeCode =
    captureEntry?.payload?.cardSchemeCode || authEntry?.payload?.cardSchemeCode || initEntry?.payload?.cardSchemeCode;
  const noOfInstallments = parseInt(authEntry?.payload?.installmentPeriod || 0);
  const interestRate = authEntry?.payload?.interestRate || 0;

  if (cardNo && cardSchemeCode) {
    return {
      cardNo,
      cardSchemeCode,
      noOfInstallments,
      interestRate,
    };
  } else {
    //try with token as fallback
    const cardId = initEntry?.payload?.cardId || authEntry?.payload?.cardId;
    if (cardId) {
      const card = creditPmp?.additionalInfo?.cardInfoList?.find((c) => c.id === cardId);
      if (card) {
        return {
          cardNo: card.cardNo,
          cardSchemeCode: card.cardSchemeCode,
          noOfInstallments,
          interestRate,
        };
      }
      return undefined;
    }
    return undefined;
  }
}

//sort rules:
// Most recently added cards at top of list, created date
// Active cards shown first, then expired
// Update to 6 cards are shown in this list view
//[a,b] > 0 => [b,a]
export function sortCards({ cards, selected }: { cards: Array<CardInfo>; selected?: string }) {
  let defaultCard = {} as CardInfo;

  if (cards && cards.length > 0) {
    // To store default card & push to the start after sorting
    cards.forEach((card: CardInfo, index: number) => {
      if (card.isDefault) {
        defaultCard = cards.splice(index, 1)?.[0];
      }
    });
    cards.sort((a: CardInfo, b: CardInfo) => {
      const cardIdA = Number(a.id === selected);
      const cardIdB = Number(b.id === selected);
      if (cardIdA > cardIdB) {
        return -1;
      } else if (cardIdA < cardIdB) {
        return 1;
      } else {
        const expiryA = Number(isCardExpired(a.panExpiry));
        const expiryB = Number(isCardExpired(b.panExpiry));
        if (expiryA > expiryB) {
          return 1;
        } else if (expiryA < expiryB) {
          return -1;
        } else {
          const d1: Date = a?.metadata?.createdAt ? new Date(a?.metadata?.createdAt) : new Date();
          const d2: Date = b?.metadata?.createdAt ? new Date(b?.metadata?.createdAt) : new Date();
          if (d1 > d2) {
            return -1;
          } else {
            return 0;
          }
        }
      }
    });
    if (defaultCard?.id) {
      cards.unshift(defaultCard);
    }
  }
  return [...cards];
}

export function filterPaymentOptions(paymentOptions: PaymentOption[]) {
  let ampointOption: PaymentOption | undefined;
  const internalOptions = [];
  const externalOptions = [];
  for (const option of paymentOptions) {
    if (option.paymentMethod.code === `${PaymentMethodId.AmwayPoints}`) {
      ampointOption = option;
    } else if (option.paymentMethod.type === PaymentMethodTypeEnum.Internal) {
      internalOptions.push(option);
    } else {
      externalOptions.push(option);
    }
  }
  return {
    ampointOption,
    internalOptions,
    externalOptions,
  };
}

export function remainingExternalAmount(
  transactions: UITransactionRequest[],
  paymentOptions: PaymentOption[],
  amount: Amount,
  transformedOptions?: ReturnType<typeof getTransformedPaymentOptions>,
) {
  const transformedOption = transformedOptions ?? getTransformedPaymentOptions(paymentOptions);
  const internalAmount = transactions
    .filter((t) => transformedOption[t?.paymentMethodProviderId] === PaymentMethodTypeEnum.Internal)
    .reduce((acc, t) => acc + (t.amount ? t.amount.value : 0), 0);
  return formatNumber2Decimal(amount.value - internalAmount);
}

export function getTransactionsOfPaymentMethod(transactions: UITransactionRequest[], paymentOption: PaymentOption) {
  return transactions?.find(
    (t: UITransactionRequest) => t?.paymentMethodProviderId === paymentOption?.paymentMethodProviderId,
  );
}

export function filterBankTransferOption(o: PaymentOption[]) {
  o.filter((o: PaymentOption) => o.paymentMethod?.isBankTransfer);
}

/**
 * Lazy load a component
 */
export const lazyLoad = <T extends React.ComponentType<AppState>>(
  importFn: () => Promise<{ default: T }>,
  fallback: React.ReactNode,
) => {
  const LazyComponent = lazy(importFn);

  // eslint-disable-next-line react/display-name
  return (props: ComponentProps<T>) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

export const filterBankAccountUsage = (bankDetails: BankAccountDetails[]) => {
  const bonusAcct = bankDetails?.filter((bank) => bank?.accountUsage === BankAccountUsage.Bonus);
  const refundAcct = bankDetails?.filter((bank) => bank?.accountUsage === BankAccountUsage.Refund);
  return { bonusAcct, refundAcct };
};

/*get Bank Names Translations */
export const getListWithLocaliseBankName = async (bankList: BankDetails[]) => {
  if (bankList.length > 0) {
    const bankIds: (string | undefined)[] = [];
    bankList.forEach((bank) => bankIds.push(`"${encodeURIComponent(bank.bankId as string)}"`));
    try {
      const cmsResponse = (await cmsApi.fetchAllContent({
        uId: 'bank_detail',
        params: [
          {
            key: 'query',
            value: `{"bank_id":{"$in":[${bankIds}]}}`,
          },
        ],
      })) as BankDetailCMSResponseType;
      return bankList.map((bank) => {
        const bankLocalisedObj = cmsResponse.find((resBank) => resBank.bank_id === (bank.bankId as unknown as string));
        return {
          ...bank,
          bankName: bankLocalisedObj?.bank_name ?? '',
          acctNoErrorMessage: bankLocalisedObj?.bank_error_message,
        };
      });
    } catch (error) {
      ConsoleLogger.error(error);
      return [];
    }
  } else {
    return [];
  }
};

export const billingAddressTransformer = (data?: Contact) => ({
  billingAddress1: data?.addrLineOne ?? '',
  billingAddress2: data?.addrLineTwo ?? '',
  billingCity: data?.city ?? '',
  billingState: data?.state ?? '',
  billingPostcode: data?.postalCode ?? '',
  billingCountry: data?.country ?? '',
});

export const shippingAddressTransformer = (data?: Contact) => ({
  shippingAddress1: data?.addrLineOne ?? '',
  shippingAddress2: data?.addrLineTwo ?? '',
  shippingCity: data?.city ?? '',
  shippingState: data?.state ?? '',
  shippingPostcode: data?.postalCode ?? '',
  shippingCountry: data?.country ?? '',
});

export const getRedirectionUrl = (transactions: PaymentTransaction[]) => {
  const inititatedTrxn = transactions?.find(
    (trxn: PaymentTransaction) => trxn.status == PaymentTransactionStatus.Initiated,
  );

  const entryObj = inititatedTrxn?.entries?.find(
    (entry: { entryType: string }) => entry.entryType == PaymentEntryType.Initiate,
  );

  return entryObj?.payload?.directPaymentUrl;
};

export const bankAccountNumberTransformer = (bankId: string, accountNumber: string) => {
  //Handling for GSB bank and BAC ATNG-5167
  if (bankId === 'GSB' && accountNumber.length === 12) {
    //If length = 12 digits → Add prefix “999” to the account number
    return `999${accountNumber}`;
  } else if (bankId === 'BAC') {
    if (accountNumber.length === 12) {
      //If account number length = 12 digits
      return accountNumber?.slice(1);
    } else if (accountNumber.length === 10) {
      //If account number length = 10 digits → Add prefix “0”
      return `0${accountNumber}`;
    }
  }
  return accountNumber;
};

export const enableBonusEditFlow = (accountStatus: string) => {
  const transLatedStatus = accountStatus?.replaceAll(' ', '')?.toLocaleUpperCase();
  return (
    transLatedStatus === AccountStatusColorEnum.EXPIRED ||
    transLatedStatus === AccountStatusColorEnum.INVALID ||
    transLatedStatus === AccountStatusColorEnum.REJECTED ||
    transLatedStatus === AccountStatusColorEnum.REJECTEDINVALIDACCTYPE ||
    transLatedStatus === AccountStatusColorEnum.REJECTEDNODETAIL ||
    transLatedStatus === AccountStatusColorEnum.REJECTEDNOSIGNATURE ||
    transLatedStatus === AccountStatusColorEnum.RJTINVDTYP ||
    transLatedStatus === AccountStatusColorEnum.RJCTNODTIL ||
    transLatedStatus === AccountStatusColorEnum.RJCTNOSIGN
  );
};
